/**
 * The files a knowledge base takes (design §0.1), as the `knowledge-base`
 * function has them (supabase/functions/knowledge-base/file-types.ts and its
 * UNSUPPORTED_FILE_TYPE sentence) — change both together. The upload dialog
 * refuses any other file before a request; the server picks the content type
 * the file is sent with, from the same table.
 */

export const FILE_FORMATS = [
  { format: "PDF", extensions: [".pdf"], contentType: "application/pdf" },
  {
    format: "DOCX",
    extensions: [".docx"],
    contentType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  { format: "DOC", extensions: [".doc"], contentType: "application/msword" },
  { format: "ODT", extensions: [".odt"], contentType: "application/vnd.oasis.opendocument.text" },
  { format: "RTF", extensions: [".rtf"], contentType: "application/rtf" },
  { format: "EPUB", extensions: [".epub"], contentType: "application/epub+zip" },
  { format: "TXT", extensions: [".txt"], contentType: "text/plain" },
  { format: "MD", extensions: [".md", ".markdown"], contentType: "text/markdown" },
  { format: "HTML", extensions: [".html", ".htm"], contentType: "text/html" },
  { format: "XML", extensions: [".xml"], contentType: "application/xml" },
  { format: "JSON", extensions: [".json"], contentType: "application/json" },
  { format: "YAML", extensions: [".yaml", ".yml"], contentType: "application/yaml" },
  { format: "CSV", extensions: [".csv"], contentType: "text/csv" },
  {
    format: "XLSX",
    extensions: [".xlsx"],
    contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
] as const;

export const UNSUPPORTED_FILE_TYPE =
  "This file type is not supported. Upload PDF, Word (DOCX or DOC), ODT, RTF, EPUB, TXT, Markdown, HTML, XML, JSON, YAML, Excel (XLSX) or CSV.";

/** What the file picker offers. */
export const ACCEPTED_EXTENSIONS = FILE_FORMATS.flatMap((type) => type.extensions).join(",");

const CONTENT_TYPES = new Map<string, string>(
  FILE_FORMATS.flatMap((type) => type.extensions.map((extension) => [extension, type.contentType] as const)),
);

/** A file name's extension from its last dot, lowercased (".pdf"); null for a name without one. */
export function extensionOf(fileName: string): string | null {
  const extension = /\.[^.]+$/.exec(fileName);
  return extension ? extension[0].toLowerCase() : null;
}

/** The content type of a file name's extension, in any case; null for any other file. */
export function contentTypeOf(fileName: string): string | null {
  return CONTENT_TYPES.get(extensionOf(fileName)) ?? null;
}

/** "PDF", "DOCX"…: a file's format, from the content type it was stored with; any other type as it is. */
export function formatOf(contentType: string): string {
  return FILE_FORMATS.find((type) => type.contentType === contentType)?.format ?? contentType;
}
